/**
 * The conversations LEFT while the device was offline, owed a cache removal
 * once it is back online (`spec-1069-offline-keeps-visited-threads.md`,
 * 2026-09-26).
 *
 * ⚠️ SCOPED TO THE QUERY CLIENT, NOT TO THE PANEL'S SESSION (review,
 * 2026-09-26). The record describes the CACHE, and the cache is the client's:
 * the session is minted per request and viewer, so a record held there was
 * thrown away by a request switch or the screen unmounting while the entries
 * it named stayed cached. Back online and back at the first request, the
 * reopen then showed the kept pages at once and re-walked them, which is the
 * stale flash Story 6.4 D3 removes entries to prevent. Keyed on the client,
 * the record outlives every mount, session and screen that can see those
 * entries, and any mount's online run settles it. A `WeakMap`, so a
 * discarded client (the harness mints one per render) takes its record with
 * it, the `uploadOverlay.ts` precedent.
 *
 * The keys carry the thread and the viewer, not the request, so the record
 * spans requests; nothing needs to tell them apart, since every entry in it
 * is owed the same removal.
 */
import { hashKey, type QueryClient } from '@tanstack/react-query';

type CacheKey = readonly unknown[];

export interface KeptOffline {
  current: readonly CacheKey[];
}

const records = new WeakMap<QueryClient, KeptOffline>();

/** This client's record, minted empty on first use. */
export const keptOfflineFor = (client: QueryClient): KeptOffline => {
  let record = records.get(client);
  if (!record) {
    record = { current: [] };
    records.set(client, record);
  }
  return record;
};

/** Whether `key` is in this client's record. */
export const isKeptOffline = (client: QueryClient, key: CacheKey): boolean => {
  const hash = hashKey(key);
  return keptOfflineFor(client).current.some(k => hashKey(k) === hash);
};
